import {
  CalculatedCostState,
  Currency,
  getFormatter,
  isDefined,
  isValidNumber,
  Keys,
  Loadable,
  NbSelectItemOption,
  useLoadableState,
} from '@eih-netbroker/common';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as RightIcon } from '../../../../assets/icons/arrow-right-line.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbEndAdornment } from '../../../../shared/components/end-adornment/EndAdornment';
import { NbIndefiniteLoader } from '../../../../shared/components/loader/Loader';
import { NbNumericFormat } from '../../../../shared/components/numeric-format/numericFormat';
import { NbSelect } from '../../../../shared/components/select/Select';
import { NbTextField } from '../../../../shared/components/text-field/TextField';
import { useInventory } from '../../../../shared/services/inventory/useInventory';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { ValidatedOrder } from '../../services/order/useNormalOrderView';
import { useOrderCurrencyExchange } from '../../services/order/useOrderCurrencyExchange';
import { OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext } from '../OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider';
import { OrderExchangeValidationError, useOrderCurrencyExchangeState } from '../OrderCurrencyExchangeStateContext';
import classes from './OrderExchange.module.scss';
import { OrderFormData } from './OrderForm';

const SourceAndTargetCurrencies = ({ order }: { order: OrderFormData }) => {
  const { translation } = useLocale();
  const { currencyExchangeBalance, hasError } = useOrderCurrencyExchangeState();
  const { clearValidationError, selectedSource, setSelectedSource, setExchangeRate } = useOrderCurrencyExchangeState();
  const { getBalanceForOrder, getExchangeSettlementDate, setCurrencyExchangeBalance, setExchangeSettlementDate } =
    useOrderCurrencyExchange();
  const {
    orderCurrencyExchangeSourceCurrencies: sourceCurrencies,
    orderCurrencyExchangeActiveSourceCurrencies: activeSourceCurrencies,
  } = useContext(OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext);
  const { inventoryDetails } = useInventory();
  const [activeSourceCurrenciesChanged, setActiveSourceCurrenciesChanged] = useState(false);
  const { getCashInfoByCurrency } = useAccountBalance();
  const { selectedAccountOrClient } = useAccounts();
  const [sourceCurrencyOptions, setSourceCurrencyOptions] = useState<NbSelectItemOption<string>[]>([]);

  const { formatCurrency } = getFormatter();

  const removeSourceCurrencyChange = useCallback(() => {
    setSelectedSource(null);
    setExchangeRate(Loadable.uninitialized);
    clearValidationError();
    setCurrencyExchangeBalance(Loadable.uninitialized);
    setExchangeSettlementDate(Loadable.uninitialized);
  }, [clearValidationError, setCurrencyExchangeBalance, setExchangeRate, setExchangeSettlementDate, setSelectedSource]);

  const cashInfo = useLoadableState(async () => {
    return getCashInfoByCurrency(selectedAccountOrClient);
  }, [selectedAccountOrClient, getCashInfoByCurrency]);

  useEffect(() => {
    if (cashInfo.isLoaded && activeSourceCurrencies.isLoaded) {
      const filteredActiveSourceCurrencies = activeSourceCurrencies
        .filter(currency => isDefined(cashInfo.cashInfo[currency as Currency]))
        .map<NbSelectItemOption<string>>(curr => ({
          id: curr ?? '',
          name: curr ?? '',
        }));

      if (selectedSource && !filteredActiveSourceCurrencies.some(_ => _.id === selectedSource)) {
        // the selected source currency is NOT in the active source currencies list anymore, remove it
        removeSourceCurrencyChange();
      }

      setSourceCurrencyOptions(filteredActiveSourceCurrencies);
    }
  }, [cashInfo, activeSourceCurrencies, selectedSource, removeSourceCurrencyChange]);

  useEffect(() => {
    if (selectedSource) {
      getBalanceForOrder(selectedSource, order as ValidatedOrder);
      getExchangeSettlementDate(order as ValidatedOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run this effect once when the component mounts
  }, [getBalanceForOrder, getExchangeSettlementDate, inventoryDetails]);

  const handleSourceCurrencyChange = useCallback(
    async (value: string) => {
      if (!order?.instrument) {
        throw new Error('Order must have instrument');
      }

      clearValidationError();
      setExchangeRate(Loadable.uninitialized);
      const selectedCurrency = sourceCurrencies.isLoaded && sourceCurrencies.find(curr => curr.currency === value);

      if (selectedCurrency) {
        setSelectedSource(selectedCurrency.currency);
        getBalanceForOrder(selectedCurrency.currency, order as ValidatedOrder);
        getExchangeSettlementDate(order as ValidatedOrder);
      }
    },
    [
      clearValidationError,
      setExchangeRate,
      sourceCurrencies,
      setSelectedSource,
      getBalanceForOrder,
      getExchangeSettlementDate,
      order,
    ],
  );

  const validateUpdatedSourceCurrencyList = async () => {
    if (activeSourceCurrenciesChanged && activeSourceCurrencies.isLoaded) {
      setActiveSourceCurrenciesChanged(false);
      // TODO NN-2576 - check if the selected source currency is in the active source currencies list
      // if (selectedSource && !activeSourceCurrencies.includes(selectedSource)) {
      //   // the selected source currency is NOT in the active source currencies list anymore close the currency exchange with an error
      //   await openNoExchangePeriodDialog(selectedSource);
      // } else if (selectedSource && activeSourceCurrencies.includes(selectedSource)) {
      //   // load the target currency list
      //   const targetCurrencyList = await loadTargetCurrencies(selectedSource);
      //
      //   // check for the selected target currency
      //   if (!targetCurrencyList || targetCurrencyList.length === 0) {
      //     // no target currency list available, close the currency exchange with an error
      //     await openNoExchangePeriodDialog(selectedSource);
      //   } else if (selectedTarget && !targetCurrencyList.some(_ => _.currency === selectedTarget)) {
      //     // the selected target currency is NOT in the target currency list anymore, close the currency exchange with an error
      //     await openNoExchangePeriodDialog(`${selectedSource}/${selectedTarget}`);
      //   } else {
      //     // otherwise we don't need to do anything
      //   }
      // }
    }
  };

  useEffect(() => {
    setActiveSourceCurrenciesChanged(true);
  }, [activeSourceCurrencies]);

  useEffect(() => {
    validateUpdatedSourceCurrencyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger this hook only when source currencies have been changed
  }, [activeSourceCurrenciesChanged]);

  if (!order.instrument) {
    return <></>;
  }

  const targetCurrencyOptions = [
    {
      id: order.instrument.currency,
      name: order.instrument.currency,
    },
  ];

  return (
    <>
      <div>
        <div className={cssWhen(classes.exchangeRow, classes.exchangeSelects)}>
          <div className={classes.exchangeSelect}>
            <span className={classes.exchangeLabel}>{translation.currencyChange.sourceCurrency}</span>
            <NbSelect
              options={sourceCurrencyOptions}
              value={selectedSource || ''}
              onChange={e => handleSourceCurrencyChange(e.target.value)}
              size={'small'}
              isLoading={sourceCurrencies.isLoading}
              emptyValueText={translation.currencyChange.selectSourceCurrency}
              className={cssWhen([
                hasError(Keys.CurrencyExchange.Errors.DEVIZAVALTAS_ELLENORZES_NYITO_ARFOLYAM_HIANYZIK) ||
                  hasError(Keys.CurrencyExchange.Errors.DEVIZAVALTAS_ELLENORZES_ARFOLYAM_FRISSITES_HIBA),
                classes.exchangeSourceCurrencyIsError,
              ])}
              disabled={activeSourceCurrencies.isLoaded && activeSourceCurrencies.length === 0}
              errorMessage={
                activeSourceCurrencies.isLoaded && activeSourceCurrencies.length === 0
                  ? 'HIBA! Nincs valtasi idoszak'
                  : ''
              }
            />
          </div>

          <div className={classes.exchangeRightIcon}>
            <RightIcon />
          </div>

          <div className={classes.exchangeSelect}>
            <span className={classes.exchangeLabel}>{translation.currencyChange.targetCurrency}</span>
            <NbSelect
              value={order.instrument.currency}
              options={targetCurrencyOptions}
              size={'small'}
              disabled={true}
              className={cssWhen([
                hasError(Keys.CurrencyExchange.Errors.DEVIZAVALTAS_ELLENORZES_NYITO_ARFOLYAM_HIANYZIK) ||
                  hasError(Keys.CurrencyExchange.Errors.DEVIZAVALTAS_ELLENORZES_ARFOLYAM_FRISSITES_HIBA),
                classes.exchangeSourceCurrencyIsError,
              ])}
            />
          </div>
        </div>

        {selectedSource && (
          <>
            {currencyExchangeBalance.isLoading && <NbIndefiniteLoader isContrast={true} />}
            {currencyExchangeBalance.isLoaded && isValidNumber(currencyExchangeBalance.investableAmount) && (
              <div
                className={cssWhen(classes.exchangeSelectBalanceInfo, [
                  hasError(OrderExchangeValidationError.NotEnoughCurrencyBalance),
                  classes.exchangeSelectBalanceInfoIsError,
                ])}
              >
                {translation.currencyChange.balance}:{' '}
                {formatCurrency(currencyExchangeBalance.investableAmount, selectedSource)}
              </div>
            )}
            {(currencyExchangeBalance.hasError ||
              (currencyExchangeBalance.isLoaded && !isValidNumber(currencyExchangeBalance.investableAmount))) && (
              <div className={classes.exchangeSelectBalanceInfo}>{translation.currencyChange.balance}: -</div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export type SourceAndTargetAmountProps = {
  order: OrderFormData;
  transactionValueGross: CalculatedCostState;
};

export const SourceAndTargetAmount = ({ order, transactionValueGross }: SourceAndTargetAmountProps) => {
  if (!order.instrument) {
    throw new Error('Instrument is not defined');
  }

  const { selectedSource, sourceAmount, targetAmount, exchangeRate, hasError } = useOrderCurrencyExchangeState();

  return (
    <>
      <div className={cssWhen(classes.exchangeRow, classes.exchangeInputs)}>
        <div className={classes.exchangeInput}>
          <NbNumericFormat
            size={'small'}
            InputProps={{
              endAdornment: (
                <NbEndAdornment
                  text={selectedSource || ''}
                  placement={'end'}
                />
              ),
            }}
            className={cssWhen([
              hasError(OrderExchangeValidationError.NotEnoughCurrencyBalance),
              classes.exchangeInputIsError,
            ])}
            customInput={NbTextField}
            value={sourceAmount}
            disabled={true}
            isLoading={exchangeRate.isLoading}
          />
        </div>

        <div className={classes.exchangeInput}>
          <NbNumericFormat
            size={'small'}
            InputProps={{
              endAdornment: (
                <NbEndAdornment
                  text={order.instrument.currency}
                  placement={'end'}
                />
              ),
            }}
            className={cssWhen([
              hasError(OrderExchangeValidationError.NotEnoughCurrencyBalance),
              classes.exchangeInputIsError,
            ])}
            customInput={NbTextField}
            value={targetAmount}
            isLoading={transactionValueGross.isLoading}
            disabled={true}
          />
        </div>
      </div>
    </>
  );
};

export const OrderCurrencyExchange = {
  SourceAndTargetCurrencies,
  SourceAndTargetAmount,
};
